/**
 * SettingsSection — onglet "Paramètres" d'une session opérateur.
 *
 * Extrait de app.tsx (Lot 5 du plan UI/UX) — ~535 LOC déplacées hors du
 * shell racine pour ramener app.tsx sous la barre des 1100 lignes et
 * isoler les 3 sous-sections (Affectation / Plugins / Agent) qui faisaient
 * du props drilling à 3 niveaux dans le fichier d'origine.
 *
 * Aucun changement de comportement : mêmes endpoints REST, mêmes commandes
 * WS, mêmes signaux. Seul le découpage en modules change.
 */
import { createSignal, createEffect, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";
import { useWs } from "./shared/ws";
import { useConfirm } from "./shared/confirm-dialog";
import { KeyboardProfileEditor } from "./shared/keyboard-profile-editor";
import { useKeyboardProfile } from "./shared/use-keyboard-profile";
import PolicyEditor, { type Preset } from "./panels/policy-editor";
import type { PolicyObject } from "./shared/host-policy-schema";
import type { HostData } from "./app";

// ─── SettingsSection ────────────────────────────────────────────────────

interface SettingsSectionProps {
  host: HostData | null | undefined;
  onRefresh: () => void;
}

export function SettingsSection(props: SettingsSectionProps) {
  const [displayName, setDisplayName] = createSignal("");
  const [tagsRaw, setTagsRaw] = createSignal("");
  const [riskLevel, setRiskLevel] = createSignal("");
  const [domainFqdn, setDomainFqdn] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [msg, setMsg] = createSignal<{ kind: "ok" | "err"; text: string } | null>(null);

  // Sync les inputs avec le host quand il charge / change.
  let lastHostId = "";
  function maybeSyncFromHost() {
    const h = props.host;
    if (!h || h.hostId === lastHostId) return;
    lastHostId = h.hostId;
    setDisplayName(h.displayName || "");
    setTagsRaw(h.tags.join(", "));
    setRiskLevel(h.riskLevel || "");
    setDomainFqdn(h.domainFqdn || "");
  }

  async function save() {
    const h = props.host;
    if (!h) return;
    setSaving(true);
    setMsg(null);
    try {
      const tags = tagsRaw()
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
      const body: Record<string, unknown> = { displayName: displayName(), tags };
      if (domainFqdn()) body.domainFqdn = domainFqdn();
      if (riskLevel()) body.riskLevel = riskLevel();
      const r = await fetch(`/host/${h.hostId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text().catch(() => "");
        throw new Error(`HTTP ${r.status} ${t.slice(0, 120)}`);
      }
      setMsg({ kind: "ok", text: "Paramètres enregistrés." });
      props.onRefresh();
    } catch (e) {
      setMsg({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div class="settings-section">
      <Show when={props.host} fallback={<div class="settings-empty">Aucun host trouvé pour la session.</div>}>
        {(h) => {
          maybeSyncFromHost();
          return (
            <>
              <h2 class="settings-title">Paramètres — {h().hostname}</h2>
              <p class="settings-subtitle">
                hostId : <code>{h().hostId}</code>
              </p>

              <Show when={msg()}>
                <div class={`settings-msg settings-msg--${msg()!.kind}`}>{msg()!.text}</div>
              </Show>

              <div class="settings-grid">
                <label class="settings-field">
                  <span class="settings-field__label">Nom affiché</span>
                  <input
                    class="settings-input"
                    value={displayName()}
                    onInput={(e) => setDisplayName(e.currentTarget.value)}
                    placeholder={h().hostname}
                  />
                </label>

                <label class="settings-field">
                  <span class="settings-field__label">Tags (séparés par virgule)</span>
                  <input
                    class="settings-input"
                    value={tagsRaw()}
                    onInput={(e) => setTagsRaw(e.currentTarget.value)}
                    placeholder="vip, prod, montréal"
                  />
                </label>

                <label class="settings-field">
                  <span class="settings-field__label">Niveau de risque</span>
                  <select
                    class="settings-input"
                    value={riskLevel()}
                    onChange={(e) => setRiskLevel(e.currentTarget.value)}
                  >
                    <option value="">— Non défini —</option>
                    <option value="low">low</option>
                    <option value="medium">medium</option>
                    <option value="high">high</option>
                  </select>
                </label>

                <label class="settings-field">
                  <span class="settings-field__label">Domaine FQDN</span>
                  <input
                    class="settings-input"
                    value={domainFqdn()}
                    onInput={(e) => setDomainFqdn(e.currentTarget.value)}
                    placeholder="example.local"
                  />
                </label>

                <div class="settings-actions">
                  <button class="btn btn--primary" type="button" onClick={save} disabled={saving()}>
                    {saving() ? "Enregistrement…" : "Enregistrer"}
                  </button>
                  <button class="btn" type="button" onClick={() => props.onRefresh()}>
                    ↺ Recharger
                  </button>
                </div>

                <OperatorPreferencesSubsection />
                <AssignmentSubsection host={h()} onSaved={() => props.onRefresh()} />
                <PluginsSubsection host={h()} />
                <AgentSubsection host={h()} onSaved={() => props.onRefresh()} />
                <VaultDefaultsSubsection host={h()} onSaved={() => props.onRefresh()} />
                <HostPolicySubsection host={h()} />
              </div>
            </>
          );
        }}
      </Show>
    </div>
  );
}

// ─── Sub-sections des Paramètres ─────────────────────────────────────────

interface Client {
  id: string;
  ownerTenantId: string;
  name: string;
}
interface Site {
  id: string;
  clientId: string;
  name: string;
}
interface HostPlugin {
  pluginId: string;
  name?: string;
  channel: string;
  enabled: boolean;
  updatedAt?: string;
  version?: string;
}

function AssignmentSubsection(props: { host: HostData; onSaved: () => void }) {
  const [clientId, setClientId] = createSignal("");
  const [siteId, setSiteId] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  const [msg, setMsg] = createSignal<string | null>(null);

  createEffect(() => {
    setClientId(props.host.clientId || "");
    setSiteId(props.host.siteId || "");
  });

  const clientsQ = createQuery<Client[]>(() => ({
    queryKey: ["settings-clients"],
    queryFn: async () => {
      const r = await fetch("/api/clients", { credentials: "include" });
      if (!r.ok) return [];
      const d = await r.json();
      return d.clients || [];
    },
  }));
  const sitesQ = createQuery<Site[]>(() => ({
    queryKey: ["settings-sites"],
    queryFn: async () => {
      const r = await fetch("/api/sites", { credentials: "include" });
      if (!r.ok) return [];
      const d = await r.json();
      return d.sites || [];
    },
  }));

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const r = await fetch(`/host/${props.host.hostId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ clientId: clientId() || null, siteId: siteId() || null }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setMsg("✓ Affectation enregistrée");
      props.onSaved();
    } catch (e) {
      setMsg("✗ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SubCard title="Affectation tenant / client / site">
      <Show when={msg()}>
        <div class={`settings-sub-msg settings-sub-msg--${msg()!.startsWith("✓") ? "ok" : "err"}`}>{msg()}</div>
      </Show>
      <div class="settings-sub-meta">
        Tenant : <code>{props.host.tenantId?.slice(0, 8) || "Plateforme"}</code>
      </div>
      <label class="settings-sub-field">
        <span class="settings-sub-label">Client</span>
        <select
          class="settings-sub-input"
          value={clientId()}
          onChange={(e) => {
            setClientId(e.currentTarget.value);
            setSiteId("");
          }}
        >
          <option value="">— Aucun client —</option>
          <For each={clientsQ.data || []}>{(c) => <option value={c.id}>{c.name}</option>}</For>
        </select>
      </label>
      <label class="settings-sub-field">
        <span class="settings-sub-label">Site</span>
        <select
          class="settings-sub-input"
          value={siteId()}
          disabled={!clientId()}
          onChange={(e) => setSiteId(e.currentTarget.value)}
        >
          <option value="">— Aucun site —</option>
          <For each={(sitesQ.data || []).filter((s) => s.clientId === clientId())}>
            {(s) => <option value={s.id}>{s.name}</option>}
          </For>
        </select>
      </label>
      <button class="btn btn--primary" type="button" onClick={save} disabled={saving()}>
        {saving() ? "Enregistrement…" : "Enregistrer l'affectation"}
      </button>
    </SubCard>
  );
}

function PluginsSubsection(props: { host: HostData }) {
  const ws = useWs();
  const confirm = useConfirm();
  const [newPluginId, setNewPluginId] = createSignal("");
  const [newPluginChannel, setNewPluginChannel] = createSignal("stable");
  const [msg, setMsg] = createSignal<string | null>(null);

  const pluginsQ = createQuery<HostPlugin[]>(() => ({
    queryKey: ["settings-host-plugins", props.host.hostId],
    queryFn: async () => {
      const r = await fetch(`/host/${props.host.hostId}/plugins`, { credentials: "include" });
      if (!r.ok) return [];
      const d = await r.json();
      return (d.installed || d.plugins || d.items || []).map((p: HostPlugin & Record<string, unknown>) => ({
        ...p,
        pluginId: String(p.pluginId || p.id || ""),
        name: typeof p.name === "string" ? p.name : undefined,
        version: String(p.version || p.installed_version || ""),
      }));
    },
    refetchInterval: 10_000,
  }));

  // Plugins déclarés capables côté agent (versions reçues via WS live/plugin/capabilities).
  const reportedVersions = () => ws.state.pluginVersions || {};

  function sendCmd(action: string, extra: Record<string, unknown>) {
    ws.send({
      type: "host:command",
      hostId: props.host.hostId,
      command: { action, ...extra },
    } as never);
  }

  function install() {
    const id = newPluginId().trim();
    if (!id) return;
    sendCmd("plugin-install", { pluginId: id, channel: newPluginChannel() });
    setMsg(`✓ Demande d'installation ${id} (${newPluginChannel()}) envoyée — propagée au prochain heartbeat`);
    setNewPluginId("");
    setTimeout(() => pluginsQ.refetch(), 3000);
  }
  async function uninstall(pluginId: string) {
    const ok = await confirm.confirm({
      title: "Désinstaller le plugin",
      body: `Désinstaller le plugin "${pluginId}" du host ${props.host.hostname} ?`,
      level: "danger",
      entityName: pluginId,
      confirmLabel: "Désinstaller",
    });
    if (!ok) return;
    sendCmd("plugin-uninstall", { pluginId });
    setMsg(`✓ Désinstallation ${pluginId} demandée`);
    setTimeout(() => pluginsQ.refetch(), 3000);
  }
  function setChannel(pluginId: string, channel: string) {
    sendCmd("channel", { scope: "plugin", pluginId, channel });
    setMsg(`✓ Canal ${pluginId} → ${channel}`);
  }

  return (
    <SubCard title="Plugins installés sur le host">
      <Show when={msg()}>
        <div class="settings-sub-msg settings-sub-msg--ok">{msg()}</div>
      </Show>
      <table class="rmm-table settings-plugins-table">
        <thead>
          <tr>
            <th>Plugin</th>
            <th>Canal</th>
            <th>Version</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <Show
            when={(pluginsQ.data || []).length > 0}
            fallback={
              <tr>
                <td colspan="4" class="settings-plugins-empty">
                  Aucun plugin installé sur ce host.
                </td>
              </tr>
            }
          >
            <For each={pluginsQ.data || []}>
              {(p) => (
                <tr>
                  <td class="settings-plugins-id">{p.name || p.pluginId}</td>
                  <td>
                    <select
                      class="settings-sub-input settings-sub-input--inline"
                      value={p.channel}
                      onChange={(e) => setChannel(p.pluginId, e.currentTarget.value)}
                    >
                      <option value="stable">stable</option>
                      <option value="beta">beta</option>
                      <option value="alpha">alpha</option>
                    </select>
                  </td>
                  <td class="settings-plugins-version">{p.version || reportedVersions()[p.pluginId] || "—"}</td>
                  <td>
                    <button class="btn btn--xs btn--danger" type="button" onClick={() => uninstall(p.pluginId)}>
                      Désinstaller
                    </button>
                  </td>
                </tr>
              )}
            </For>
          </Show>
        </tbody>
      </table>
      <div class="settings-plugins-add">
        <input
          class="settings-sub-input settings-plugins-add__id"
          placeholder="ID du plugin à installer (ex. choco)"
          value={newPluginId()}
          onInput={(e) => setNewPluginId(e.currentTarget.value)}
        />
        <select
          class="settings-sub-input settings-sub-input--inline"
          value={newPluginChannel()}
          onChange={(e) => setNewPluginChannel(e.currentTarget.value)}
        >
          <option value="stable">stable</option>
          <option value="beta">beta</option>
          <option value="alpha">alpha</option>
        </select>
        <button class="btn btn--primary" type="button" onClick={install}>
          + Installer
        </button>
      </div>
    </SubCard>
  );
}

interface HostVersionInfo {
  channel: string;
  version: string | null;
  publishedAt: string | null;
}

function AgentSubsection(props: { host: HostData; onSaved: () => void }) {
  const ws = useWs();
  const [channel, setChannel] = createSignal<"stable" | "beta" | "alpha">("stable");
  const [msg, setMsg] = createSignal<string | null>(null);
  const [savingChannel, setSavingChannel] = createSignal(false);
  const [versionCheckBusy, setVersionCheckBusy] = createSignal(false);
  const [latestVersion, setLatestVersion] = createSignal<HostVersionInfo | null>(null);

  // Sync canal depuis le host. hostModules.channel est la source de vérité
  // côté DB ; on tolère l'absence (défaut stable).
  createEffect(() => {
    setChannel((props.host as HostData & { channel?: "stable" | "beta" | "alpha" }).channel || "stable");
  });

  function sendCmd(action: string, extra: Record<string, unknown> = {}) {
    ws.send({ type: "host:command", hostId: props.host.hostId, command: { action, ...extra } } as never);
  }

  // Vérifie la version disponible sur le canal sélectionné.
  async function checkVersion(showMessage = false) {
    setVersionCheckBusy(true);
    try {
      const res = await fetch(`/api/host-version?channel=${encodeURIComponent(channel())}`, {
        credentials: "include",
        headers: { accept: "application/json" },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as Record<string, unknown>;
      const v = typeof data.version === "string" ? data.version : null;
      const p = typeof data.publishedAt === "string" ? data.publishedAt : null;
      setLatestVersion({ channel: channel(), version: v, publishedAt: p });
      if (showMessage) setMsg("✓ Version disponible vérifiée.");
    } catch (e) {
      setMsg("✗ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setVersionCheckBusy(false);
    }
  }

  // Applique le canal : PATCH /host/:id (persisté en base) + host:command
  // (propagation immédiate au prochain heartbeat via host_modules.channel).
  async function applyChannel() {
    setSavingChannel(true);
    setMsg(null);
    try {
      const res = await fetch(`/host/${props.host.hostId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ channel: channel() }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      if (ws.isOpen()) {
        sendCmd("channel", { scope: "host", channel: channel() });
      }
      setMsg(`✓ Canal core → ${channel()} — propagé au prochain heartbeat`);
      props.onSaved();
      // Re-vérifie la version disponible sur le nouveau canal.
      void checkVersion();
    } catch (e) {
      setMsg("✗ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSavingChannel(false);
    }
  }
  function update() {
    sendCmd("update");
    setMsg("✓ Mise à jour forcée demandée — l'agent télécharge et redémarre");
  }
  function refreshComponents() {
    sendCmd("refresh-components");
    setMsg("✓ Re-téléchargement Agent + Helper demandé — le ComponentRefresher applique au prochain heartbeat");
  }
  function selfUpdate() {
    sendCmd("self-update");
    setMsg("✓ Updater Service signalé — il va vérifier /api/host-version immédiatement");
  }

  function versionStatus(): string {
    const current = props.host.version;
    const latest = latestVersion()?.version || undefined;
    if (!current || !latest) return "À vérifier";
    return current === latest ? "À jour" : "Mise à jour disponible";
  }

  // Première vérification de version au montage.
  createEffect(() => {
    void props.host.hostId;
    void checkVersion();
  });

  return (
    <SubCard title="Agent : version et mise à jour">
      <Show when={msg()}>
        <div class="settings-sub-msg settings-sub-msg--ok">{msg()}</div>
      </Show>
      <div class="settings-agent-version">
        Version installée : <code>{props.host.version || "—"}</code> · Canal actuel :{" "}
        <code>{(props.host as HostData & { channel?: string }).channel || "stable"}</code>
      </div>
      <div class="settings-agent-row">
        <span class="settings-sub-label" title="Canal de mise à jour des binaires core (Host, Agent, Helper).">
          Nouveau canal :
        </span>
        <select
          class="settings-sub-input settings-sub-input--inline"
          value={channel()}
          onChange={(e) => setChannel(e.currentTarget.value as "stable" | "beta" | "alpha")}
        >
          <option value="stable">stable</option>
          <option value="beta">beta</option>
          <option value="alpha">alpha</option>
        </select>
        <button class="btn" type="button" onClick={applyChannel} disabled={savingChannel()}>
          {savingChannel() ? "Application…" : "Appliquer canal"}
        </button>
      </div>
      <div class="settings-agent-version-box">
        <div>
          <span>Disponible ({channel()})</span>
          <strong>{latestVersion()?.version ?? (versionCheckBusy() ? "…" : "—")}</strong>
        </div>
        <div>
          <span>État</span>
          <strong>{versionStatus()}</strong>
        </div>
        <Show when={latestVersion()?.publishedAt}>
          <div>
            <span>Publiée</span>
            <strong>{new Date(latestVersion()!.publishedAt!).toLocaleDateString()}</strong>
          </div>
        </Show>
      </div>
      <div class="settings-agent-actions">
        <button class="btn" type="button" onClick={() => checkVersion(true)} disabled={versionCheckBusy()}>
          {versionCheckBusy() ? "Vérification…" : "Vérifier version"}
        </button>
        <button class="btn btn--primary" type="button" onClick={update}>
          ↻ Forcer mise à jour
        </button>
        <button
          class="btn"
          type="button"
          onClick={refreshComponents}
          title="Force le re-téléchargement de RemoteWS-Agent et RemoteWS.Agent.Helper depuis le relay sans toucher au Host. Utile si seuls Agent/Helper ont été rebuilds."
        >
          ⇣ Recharger Agent+Helper
        </button>
        <button
          class="btn"
          type="button"
          onClick={selfUpdate}
          title="Signale au RemoteWSUpdater Windows Service de check immédiatement /api/host-version sans attendre le timeout horaire. Tire le nouveau Host binary si différent."
        >
          ⚡ Forcer Updater Service
        </button>
      </div>
    </SubCard>
  );
}

// ─── VaultDefaultsSubsection : credentials VOLT par défaut par usage ──────

interface VaultCred {
  id: string;
  label: string;
  kind: string;
}

const VAULT_USAGES: Array<{ key: string; label: string }> = [
  { key: "rdp", label: "RDP" },
  { key: "watchguard-cli", label: "WatchGuard CLI" },
  { key: "watchguard-portal", label: "WatchGuard Portail" },
  { key: "ssh", label: "SSH" },
];

function VaultDefaultsSubsection(props: { host: HostData; onSaved: () => void }) {
  const [defaults, setDefaults] = createSignal<Record<string, string>>({});
  const [credsList, setCredsList] = createSignal<VaultCred[]>([]);
  const [credsLoading, setCredsLoading] = createSignal(false);
  const [saving, setSaving] = createSignal(false);
  const [msg, setMsg] = createSignal<string | null>(null);

  // Re-sync depuis le host quand il change.
  createEffect(() => {
    void props.host.hostId;
    setDefaults({ ...(props.host.vaultDefaults || {}) });
    void loadCreds();
  });

  async function loadCreds() {
    setCredsLoading(true);
    try {
      const params = new URLSearchParams();
      if (props.host.hostId) params.set("hostId", props.host.hostId);
      const res = await fetch(`/api/credential-vault/credentials?${params.toString()}`, {
        credentials: "include",
      });
      const data = await res.json();
      const items: Array<{ id: string; label?: string | null; username?: string | null; kind?: string | null }> =
        data.items || [];
      setCredsList(items.map((c) => ({ id: c.id, label: c.label || c.username || c.id, kind: c.kind || "generic" })));
    } catch {
      setCredsList([]);
    } finally {
      setCredsLoading(false);
    }
  }

  function setUsage(key: string, value: string) {
    setDefaults((d) => {
      const next = { ...d };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
  }

  async function save() {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(`/host/${props.host.hostId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ vaultDefaults: defaults() }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      setMsg("✓ Credentials VOLT enregistrés.");
      props.onSaved();
    } catch (e) {
      setMsg("✗ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setSaving(false);
    }
  }

  return (
    <SubCard title="🔑 Credentials VOLT par défaut">
      <Show when={msg()}>
        <div class="settings-sub-msg settings-sub-msg--ok">{msg()}</div>
      </Show>
      <p class="settings-vault-hint">
        Pré-sélectionne un credential du coffre pour chaque usage. L'opérateur garde toujours la main pour saisir un mot
        de passe manuel ou changer de credential.
      </p>
      <div class="settings-vault-grid">
        <For each={VAULT_USAGES}>
          {(u) => (
            <label class="settings-sub-field">
              <span class="settings-sub-label">{u.label}</span>
              <select
                class="settings-sub-input"
                value={defaults()[u.key] || ""}
                onChange={(e) => setUsage(u.key, e.currentTarget.value)}
              >
                <option value="">— Aucun —</option>
                <For each={credsList()}>{(c) => <option value={c.id}>{c.label}</option>}</For>
              </select>
            </label>
          )}
        </For>
      </div>
      <Show when={credsLoading()}>
        <small style={{ color: "var(--text-4)" }}>Chargement des credentials…</small>
      </Show>
      <button class="btn btn--primary" type="button" onClick={save} disabled={saving()}>
        {saving() ? "Enregistrement…" : "Enregistrer les defaults"}
      </button>
    </SubCard>
  );
}

// ─── HostPolicySubsection : override de stratégie spécifique au host ──────

function HostPolicySubsection(props: { host: HostData }) {
  const [open, setOpen] = createSignal(false);
  const [initial, setInitial] = createSignal<PolicyObject>({});
  const [busy, setBusy] = createSignal(false);
  const [msg, setMsg] = createSignal<string | null>(null);

  const presetsQ = createQuery<Preset[]>(() => ({
    queryKey: ["host-policy-presets"],
    queryFn: async () => {
      const r = await fetch("/api/host-policy/presets", { credentials: "include" });
      if (!r.ok) return [];
      const d = (await r.json()) as { presets?: Preset[] };
      return d.presets || [];
    },
  }));

  async function openEditor() {
    // La policy override du host est exposée par GET /host/list via
    // serializeHostRow (lib/host-presenter.js: policy: row.policy || {}).
    const current = (props.host as HostData & { policy?: PolicyObject }).policy || {};
    setInitial(current);
    setOpen(true);
  }

  async function save(patch: PolicyObject) {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fetch(`/host/${encodeURIComponent(props.host.hostId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json", accept: "application/json" },
        body: JSON.stringify({ policy: patch }),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status}${txt ? ` — ${txt.slice(0, 120)}` : ""}`);
      }
      setMsg("✓ Stratégie host enregistrée — propagation au prochain heartbeat.");
      setOpen(false);
    } catch (e) {
      setMsg("✗ " + (e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <SubCard title="🔐 Stratégie (override host)">
      <p class="settings-vault-hint">
        Override de stratégie spécifique à ce host (table <code>host_modules.policy</code>). Surchappe la policy du
        tenant/client/site.
      </p>
      <Show when={msg()}>
        <div class="settings-sub-msg settings-sub-msg--ok">{msg()}</div>
      </Show>
      <button class="btn" type="button" onClick={openEditor}>
        ✎ Éditer l'override
      </button>
      <Show when={open()}>
        <div class="settings-policy-modal-overlay" onClick={() => !busy() && setOpen(false)}>
          <div class="settings-policy-modal" onClick={(e) => e.stopPropagation()}>
            <PolicyEditor
              scopeType="host"
              scopeId={props.host.hostId}
              scopeLabel={props.host.displayName || props.host.hostname}
              initialPolicy={initial()}
              presets={presetsQ.data || []}
              hostId={props.host.hostId}
              onSave={save}
              onCancel={() => setOpen(false)}
            />
          </div>
        </div>
      </Show>
    </SubCard>
  );
}

// ─── OperatorPreferencesSubsection : profil clavier + clipboard (globals) ──

function OperatorPreferencesSubsection() {
  const kb = useKeyboardProfile();
  const [editorOpen, setEditorOpen] = createSignal(false);

  return (
    <SubCard title="⌨️ Préférences opérateur (clavier & clipboard)">
      <p class="settings-vault-hint">
        Profil clavier et réglages clipboard partagés entre les sessions RDP et Vidéo. Ces préférences sont stockées
        localement dans votre navigateur (elles ne dépendent pas du host).
      </p>
      <div class="settings-agent-row">
        <span class="settings-sub-label">Profil clavier actif :</span>
        <strong>
          {kb.activeProfileId() === "mac"
            ? "Mac (Cmd → Ctrl)"
            : kb.activeProfileId() === "windows"
              ? "Windows / Linux (natif)"
              : "Personnalisé"}
        </strong>
        <button class="btn btn--primary" type="button" onClick={() => setEditorOpen(true)}>
          ✎ Éditer le profil
        </button>
      </div>
      <KeyboardProfileEditor
        open={editorOpen()}
        onClose={() => setEditorOpen(false)}
        activeProfileId={kb.activeProfileId()}
        onProfileChange={kb.setActiveProfileId}
      />
    </SubCard>
  );
}

function SubCard(props: { title: string; children: unknown }) {
  return (
    <div class="settings-subcard">
      <div class="settings-subcard__title">{props.title}</div>
      {props.children as never}
    </div>
  );
}
